import { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Shield, Lock, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Badge } from '../../components/ui/Badge';
import api from '../../services/api';
import { RoleModal } from './components/RoleModal';

export default function RolesPage() {
  const { user, hasPermission, refreshUser } = useAuth();
  const { t }    = useTranslation();
  const navigate = useNavigate();

  const isAdmin = user?.company?.type === 'ADMIN';

  useEffect(() => {
    if (!isAdmin) navigate('/dashboard', { replace: true });
  }, [isAdmin, navigate]);

  const [roles, setRoles]                   = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [formModal, setFormModal]           = useState(null);
  const [confirm, setConfirm]               = useState(null);
  const [deleting, setDeleting]             = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions'),
      ]);
      setRoles(rolesRes.data);
      setAllPermissions(permsRes.data.filter((p) => p.key !== 'manage_roles'));
    } catch (e) {
      setError(e?.response?.data?.message ?? t('common.error_occurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // A role edit/delete takes effect on the backend immediately (AuthGuard
  // re-resolves the role every request) — resync our own session too, in
  // case the role just changed is the one we're currently logged in as.
  const loadAndResync = useCallback(async () => {
    await Promise.all([load(), refreshUser().catch(() => {})]);
  }, [load, refreshUser]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  async function handleDelete() {
    if (!confirm || !isUserAdmin) return;
    setDeleting(true);
    try {
      await api.delete(`/roles/${confirm._id}`);
      setConfirm(null);
      loadAndResync();
    } catch (e) {
      setError(e?.response?.data?.message ?? t('common.error_occurred'));
      setConfirm(null);
    } finally {
      setDeleting(false);
    }
  }

  const isUserAdmin = hasPermission('manage_roles');

  if (!isAdmin) return null;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-bold text-slate-900">{t('roles.title')}</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">{t('roles.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="h-9 px-3 flex items-center gap-1.5 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition"
          >
            <RefreshCw size={14} strokeWidth={1.75} />
          </button>
          {isUserAdmin && (
            <button
              onClick={() => setFormModal({ role: null })}
              className="h-9 px-4 flex items-center gap-1.5 bg-primary text-white rounded-lg text-[13px] font-semibold hover:opacity-90 transition"
            >
              <Plus size={15} strokeWidth={1.75} />
              {t('roles.new_role')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {loading ? (
        <div className="text-[13px] text-slate-400 py-10 text-center">{t('common.loading')}</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  {t('roles.col_name')}
                </th>
                <th className="text-left px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  {t('roles.col_permissions')}
                </th>
                <th className="text-right px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {roles.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-10 text-slate-400">
                    {t('roles.no_roles')}
                  </td>
                </tr>
              )}
              {roles.map((role) => (
                <tr key={role._id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Shield size={14} strokeWidth={1.75} className="text-slate-400 flex-shrink-0" />
                      <span className="font-medium text-slate-800">{role.name}</span>
                      {role.isSystem && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                          <Lock size={9} strokeWidth={2} />
                          {t('roles.system_badge')}
                        </span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-[11.5px] text-slate-400 mt-0.5 ml-[22px]">{role.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(role.permissions ?? []).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <>
                          {(role.permissions ?? []).slice(0, 4).map((p) => (
                            <Badge key={p._id ?? p} variant="info">
                              {p.nameEn ?? p.key ?? p}
                            </Badge>
                          ))}
                          {(role.permissions ?? []).length > 4 && (
                            <span className="text-[11px] text-slate-400 self-center">
                              +{(role.permissions ?? []).length - 4}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {isUserAdmin && (
                        <>
                          <button
                            onClick={() => setFormModal({ role })}
                            className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
                            title={t('common.edit')}
                          >
                            <Pencil size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            onClick={() => !role.isSystem && setConfirm(role)}
                            disabled={role.isSystem}
                            className="w-8 h-8 grid place-items-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed transition"
                            title={role.isSystem ? t('roles.cannot_delete_system') : t('common.delete')}
                          >
                            <Trash2 size={14} strokeWidth={1.75} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RoleModal
        open={Boolean(formModal)}
        onClose={() => setFormModal(null)}
        role={formModal?.role ?? null}
        allPermissions={allPermissions}
        onSaved={loadAndResync}
      />

      <ConfirmModal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={t('roles.delete_title')}
        message={t('roles.delete_message', { name: confirm?.name })}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
